import { useStore } from '@stores';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * One page of a build's jobs, filtered.
 *
 * Kept out of useMasterBuild on purpose: the build's own fields change when someone renames it,
 * the list of legs changes when someone pages or filters, and tying the two together would reload
 * the header every time the reader moved down the list.
 *
 * Filters follow the list-page convention rather than the side panel's: text is debounced and the
 * dropdowns apply on the spot, because there is room here for the list to move under the reader.
 */
export const useMasterBuildOrders = (masterBuildId?: string) => {
  const { masterBuildStore } = useStore();
  const {
    orders,
    ordersTotalCount,
    ordersSkipCount,
    ordersMaxResultCount,
    ordersCurrentPage,
    ordersLoading,
    setOrdersPage,
    setOrdersPageSize,
  } = masterBuildStore;

  const [status, setStatusValue] = useState<string[]>([]);
  const [userId, setUserIdValue] = useState<number | undefined>();
  const [jobIds, setJobIdsValue] = useState<string[]>([]);
  const [hideCanceled, setHideCanceledValue] = useState(false);
  // The service column the list is ordered on, e.g. 'code asc'. Applied server-side.
  const [sorting, setSortingValue] = useState<string | undefined>();

  const request = useMemo(
    () => ({
      masterBuildId: masterBuildId ?? '',
      status: status.length ? status.join(',') : null,
      userId,
      orderIds: jobIds.length ? jobIds.join(',') : null,
      hideCanceled,
      sorting,
      maxResultCount: ordersMaxResultCount,
      skipCount: ordersSkipCount,
    }),
    [masterBuildId, status, userId, jobIds, hideCanceled, sorting, ordersMaxResultCount, ordersSkipCount]
  );

  useEffect(() => {
    if (!masterBuildId) return;
    masterBuildStore.getOrders(request);
  }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only on the way out. This used to be the cleanup of the effect above, which made paging
  // impossible: clearOrders resets the skip count, the skip count is part of `request`, so asking
  // for page two immediately reset it and asked for page one again.
  useEffect(() => () => masterBuildStore.clearOrders(), [masterBuildStore]);

  // Deleting the last leg on the last page leaves the reader on a page that no longer exists,
  // looking at an empty list with the pager still pointing past the end. Step back to what is now
  // the last page and the effect above re-asks for it.
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(ordersTotalCount / ordersMaxResultCount));
    if (ordersCurrentPage > lastPage) setOrdersPage(lastPage);
  }, [ordersTotalCount, ordersMaxResultCount, ordersCurrentPage, setOrdersPage]);

  const refresh = useCallback(() => {
    if (masterBuildId) masterBuildStore.getOrders(request);
  }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Every filter goes back to page one, or a narrowed list lands on a page that no longer exists. */
  const backToFirstPage = useCallback(() => setOrdersPage(1), [setOrdersPage]);

  const setStatus = useCallback(
    (value: string[]) => {
      setStatusValue(value);
      backToFirstPage();
    },
    [backToFirstPage]
  );

  const setUserId = useCallback(
    (value?: number) => {
      setUserIdValue(value);
      backToFirstPage();
    },
    [backToFirstPage]
  );

  /** The job picker's own filter - narrows to specific named jobs rather than a keyword. */
  const setJobIds = useCallback(
    (value: string[]) => {
      setJobIdsValue(value);
      backToFirstPage();
    },
    [backToFirstPage]
  );

  const setHideCanceled = useCallback(
    (value: boolean) => {
      setHideCanceledValue(value);
      backToFirstPage();
    },
    [backToFirstPage]
  );

  const setSorting = useCallback(
    (value?: string) => {
      setSortingValue(value);
      backToFirstPage();
    },
    [backToFirstPage]
  );

  const clearFilters = useCallback(() => {
    setStatusValue([]);
    setUserIdValue(undefined);
    setJobIdsValue([]);
    setHideCanceledValue(false);
    setSortingValue(undefined);
    backToFirstPage();
  }, [backToFirstPage]);

  return {
    orders: Array.from(orders.values()),
    totalCount: ordersTotalCount,
    currentPage: ordersCurrentPage,
    maxResultCount: ordersMaxResultCount,
    loading: ordersLoading,
    setPage: setOrdersPage,
    setPageSize: setOrdersPageSize,
    refresh,

    status,
    setStatus,
    userId,
    setUserId,
    jobIds,
    setJobIds,
    hideCanceled,
    setHideCanceled,
    sorting,
    setSorting,
    clearFilters,
    hasFilters: !!(status.length || userId !== undefined || jobIds.length || hideCanceled),
  };
};
